import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'antd';

import {
  AssignmentAdditionalInfoReplyRequestModel,
  AssignmentStageModel,
  AssignmentStatusTypeEnum,
  AssignmentTerminationModel,
  AssignmentTypeEnum,
  StageStatusEnum,
} from 'api/backofficeApiClient';
import {
  AssignmentAdminDocumentModel,
  AssignmentApproveStepModel,
  AssignmentStageEnum,
} from 'api/adminApiClient';
import {
  approveAssignmentProgressStep,
  completeAssignmentProgressStep,
  hideAssignmentPulsatingIndicator,
  sendAssignmentAdditionalInfoResponse,
  sendClassificationQuestionnaireNotification,
  sendMemberQuestionnaireNotification,
} from 'redux/reducers/assignmentSlice';
import {
  AdditionalInfoResponseModel,
  DEFAULT_ACTION_BTN_TEXT,
  getProfileProgress,
  isHardStopStage,
  isMemberResponsible,
  isStageConsideredAsCompleted,
  ProfileStatus,
  StepChangeRequestModel,
} from 'utils/profileProgressUtils/generalProgressUtils';
import {
  ClassificationQuestionnaireModal,
  MemberQuestionnaireModal,
} from 'features/assignment/Questionnaire/QuestionnaireModals';
import {
  AssignmentProgressStagesOptions,
  getAssignmentProgressStages,
  HAS_DUPLICATED_WORKER_ERROR,
} from 'utils/profileProgressUtils/assignmentProgressUtils';
import {
  BaseProgressPercentage,
  BaseProgressStep,
  BaseProgressTrackerWrapper,
} from 'components/wrappers/BaseProgressBar';
import { UserRole } from 'enums';
import BaseAlert from 'components/wrappers/BaseAlert';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import { AssignmentDetailsModel } from 'utils/assignmentUtils';
import { isAdminSelector } from 'redux/selectors/authenticationSelectors';
import { AssignmentInfoTypes } from 'features/assignment/enums';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';

import styles from './AssignmentProgressBar.module.less';

type Props = {
  assignment: AssignmentDetailsModel;
  setPrimaryDetails: (assignment: AssignmentDetailsModel) => void;
  setAdditionalDetails: (assignment: AssignmentAdminDocumentModel) => void;
  assignmentTerminationDetails?: AssignmentTerminationModel;
};

const progressStepErrorText = 'Waiting for admin response';

export default function AssignmentProgressBar(props: Props) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isAdminRole = useAppSelector(isAdminSelector);
  const [isMemberQuestionnaireModalOpen, setIsMemberQuestionnaireModalOpen] =
    useState(false);
  const [
    isClassificationQuestionnaireModalOpen,
    setIsClassificationQuestionnaireModalOpen,
  ] = useState(false);
  const [isQuestionnaireSending, setIsQuestionnaireSending] = useState(false);
  const {
    assignment,
    setPrimaryDetails,
    setAdditionalDetails,
    assignmentTerminationDetails,
  } = props;
  const {
    assignmentType,
    assignmentId,
    assignmentKey,
    stages,
    renewStatus,
    parentAssignmentKey,
    isWorkerDuplicated,
    renewKind,
    assignmentStatus,
  } = assignment;
  const isCancelledStatus =
    assignmentStatus === AssignmentStatusTypeEnum.Cancelled;
  const hasTerminationDetails = !!assignmentTerminationDetails;

  const assignmentStages = useMemo(() => {
    const stagesOptions: AssignmentProgressStagesOptions = {
      isAdmin: isAdminRole,
      stages,
      renewStatus,
      type: assignmentType as AssignmentTypeEnum,
      hasDuplicatedWorker: isWorkerDuplicated,
      renewKind: renewKind,
    };

    return getAssignmentProgressStages(stagesOptions);
  }, [
    stages,
    isAdminRole,
    assignmentType,
    renewStatus,
    isWorkerDuplicated,
    renewKind,
  ]);

  const handleRenewAssignmentStage = useCallback(
    async (renewData: AssignmentApproveStepModel, isRehireKind: boolean) => {
      const result = await dispatch(approveAssignmentProgressStep(renewData));

      if (approveAssignmentProgressStep.fulfilled.match(result)) {
        const title = isRehireKind
          ? 'Assignment successfully rehired.'
          : 'Assignment successfully renewed.';

        BaseAlert.success(title);

        navigate(
          `/assignment/${parentAssignmentKey}?section=${AssignmentInfoTypes.Primary}`,
          { replace: true },
        );
      } else {
        BaseAlert.error(result.error.message || ApiErrorMessage);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, parentAssignmentKey],
  );

  const onStepChange = useCallback(
    async (stageData: StepChangeRequestModel) => {
      const { stage, stageStatus, message: requestMessage } = stageData;

      const data = {
        assignmentId,
        currentStage: stage,
        currentStageStatus: stageStatus,
      } as AssignmentApproveStepModel;

      const isRenewOrRehire =
        (stage === AssignmentStageEnum.Renewed ||
          stage === AssignmentStageEnum.Rehired) &&
        parentAssignmentKey;

      if (isRenewOrRehire && isStageConsideredAsCompleted(stageStatus)) {
        const isRehireKind = stage === AssignmentStageEnum.Rehired;

        await handleRenewAssignmentStage(data, isRehireKind);
        return;
      }

      requestMessage && (data.message = requestMessage);

      const result = await dispatch(completeAssignmentProgressStep(data));

      if (completeAssignmentProgressStep.fulfilled.match(result)) {
        !!requestMessage && BaseAlert.success('Request sent.');
        setPrimaryDetails(result.payload);
      } else {
        BaseAlert.error(result.error.message || ApiErrorMessage);
      }
    },
    [
      assignmentId,
      dispatch,
      setPrimaryDetails,
      handleRenewAssignmentStage,
      parentAssignmentKey,
    ],
  );

  const handleStepChange = useCallback(
    (responsibleRole: UserRole, stageStatus: StageStatusEnum) =>
      async (stageData: StepChangeRequestModel) => {
        if (isWorkerDuplicated) {
          BaseAlert.error(HAS_DUPLICATED_WORKER_ERROR);
          return;
        }

        const isHardStopMemberResponsible =
          isHardStopStage(stageStatus) && isMemberResponsible(responsibleRole);

        if (isHardStopMemberResponsible) {
          await onStepChange(stageData);
          return;
        }

        switch (stageData.stage) {
          case AssignmentStageEnum.ICMemberQuestionnaireComplete:
            return setIsMemberQuestionnaireModalOpen(true);

          case AssignmentStageEnum.ICContractorQuestionnaireComplete:
            return setIsClassificationQuestionnaireModalOpen(true);
        }

        if (!isAdminRole) return;

        await onStepChange(stageData);
      },
    [onStepChange, isAdminRole, isWorkerDuplicated],
  );

  const handleSendClassificationQuestionnaireNotification = async (
    status: AssignmentStageEnum,
  ) => {
    if (isWorkerDuplicated) {
      return BaseAlert.error(HAS_DUPLICATED_WORKER_ERROR);
    }

    setIsQuestionnaireSending(true);

    let sendQuestionnaireNotificationHandler;

    switch (status) {
      case AssignmentStageEnum.ICMemberQuestionnaireComplete:
        sendQuestionnaireNotificationHandler =
          sendMemberQuestionnaireNotification;
        break;

      case AssignmentStageEnum.ICContractorQuestionnaireComplete:
        sendQuestionnaireNotificationHandler =
          sendClassificationQuestionnaireNotification;
        break;
    }

    if (!sendQuestionnaireNotificationHandler) return;

    const result = await dispatch(
      sendQuestionnaireNotificationHandler({
        assignmentKey: assignmentKey as string,
        assignmentId: assignmentId as string,
        isAdminRole,
      }),
    );

    if (sendQuestionnaireNotificationHandler.fulfilled.match(result)) {
      BaseAlert.success('A questionnaire fill in request has been sent.');
      setPrimaryDetails(result.payload);
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }

    setIsQuestionnaireSending(false);
  };

  const renderSendQuestionnaireNotificationBtn = (
    step: AssignmentStageModel,
    responsibleRole: UserRole,
  ) => {
    const { stage, stageStatus } = step;

    if (
      stage !== AssignmentStageEnum.ICMemberQuestionnaireComplete &&
      stage !== AssignmentStageEnum.ICContractorQuestionnaireComplete
    )
      return null;

    if (
      !isAdminRole ||
      (isMemberResponsible(responsibleRole) && isHardStopStage(stageStatus))
    )
      return null;

    return (
      <Button
        size="small"
        loading={isQuestionnaireSending}
        disabled={isQuestionnaireSending}
        onClick={() => handleSendClassificationQuestionnaireNotification(stage)}
      >
        Send
      </Button>
    );
  };

  const getExtraActionButton = (
    step: AssignmentStageModel,
    responsibleRole: UserRole,
  ) => {
    return <>{renderSendQuestionnaireNotificationBtn(step, responsibleRole)}</>;
  };

  const handleResponseSent = useCallback(
    async (responseInfo: AdditionalInfoResponseModel) => {
      if (!assignmentId) return;

      const { responseMessage, stage } = responseInfo;

      const data: AssignmentAdditionalInfoReplyRequestModel = {
        assignmentId,
        additionalInfoReply: responseMessage,
        currentStage: stage as AssignmentStageEnum,
      };

      const result = await dispatch(sendAssignmentAdditionalInfoResponse(data));

      if (sendAssignmentAdditionalInfoResponse.fulfilled.match(result)) {
        BaseAlert.success('Response successfully sent.');
        setPrimaryDetails(result.payload);
      } else {
        BaseAlert.error(result.error.message || ApiErrorMessage);
      }
    },
    [assignmentId, dispatch, setPrimaryDetails],
  );

  const onPulsatingHover = useCallback(
    async (stage: ProfileStatus) => {
      if (!assignmentId) return;

      const result = await dispatch(
        hideAssignmentPulsatingIndicator({
          assignmentId,
          currentStage: stage as AssignmentStageEnum,
        }),
      );

      if (hideAssignmentPulsatingIndicator.fulfilled.match(result)) {
        setPrimaryDetails(result.payload);
      } else {
        BaseAlert.error(result.error.message || ApiErrorMessage);
      }
    },
    [assignmentId, dispatch, setPrimaryDetails],
  );

  return (
    <>
      {isMemberQuestionnaireModalOpen && (
        <MemberQuestionnaireModal
          isAdmin={isAdminRole}
          setPrimaryDetails={setPrimaryDetails}
          setAdditionalDetails={setAdditionalDetails}
          assignmentId={assignmentId as string}
          assignmentKey={assignmentKey as string}
          onClose={() => setIsMemberQuestionnaireModalOpen(false)}
        />
      )}

      {isClassificationQuestionnaireModalOpen && (
        <ClassificationQuestionnaireModal
          isAdmin={isAdminRole}
          setPrimaryDetails={setPrimaryDetails}
          setAdditionalDetails={setAdditionalDetails}
          assignmentId={assignmentId as string}
          assignmentKey={assignmentKey as string}
          onClose={() => setIsClassificationQuestionnaireModalOpen(false)}
        />
      )}

      <div className={styles.progressTracker}>
        <div className={styles.progressPercentage}>
          <BaseProgressPercentage
            percent={getProfileProgress(assignmentStages)}
          />
        </div>

        <div className="page-box">
          <div className="page-inner-container">
            <BaseProgressTrackerWrapper>
              {assignmentStages.map((progressStep, index) => (
                <BaseProgressStep
                  key={index}
                  index={index}
                  isAdmin={isAdminRole}
                  {...progressStep}
                  errorText={
                    progressStep.errorText || isAdminRole
                      ? undefined
                      : progressStepErrorText
                  }
                  onComplete={handleStepChange(
                    progressStep.responsibleRole,
                    progressStep.stageStatus,
                  )}
                  actionBtnText={
                    progressStep.actionBtnText || DEFAULT_ACTION_BTN_TEXT
                  }
                  isActionEnabled={progressStep.isActionEnabled || false}
                  onRequestSent={onStepChange}
                  onResponseSent={handleResponseSent}
                  onPulsatingHover={onPulsatingHover}
                  onRequestOpenErrorMsg={
                    isWorkerDuplicated ? HAS_DUPLICATED_WORKER_ERROR : undefined
                  }
                  extraActionButton={getExtraActionButton(
                    progressStep,
                    progressStep.responsibleRole,
                  )}
                  isDisplayPopover={
                    !isCancelledStatus && !hasTerminationDetails
                  }
                />
              ))}
            </BaseProgressTrackerWrapper>
          </div>
        </div>
      </div>
    </>
  );
}
